import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiService } from '../../services/apiService'

interface PersonalStats {
  leads_assigned: number
  leads_released: number
  leads_completed: number
  leads_successful: number
  total_worked_on: number
  success_rate: number
  release_rate: number
  average_time_minutes: number
  total_emails_found: number
  by_source: {
    palladium: number
    crm: number
    api: number
  }
  queue_status: {
    available: number
    locked: number
    total: number
  }
}

interface TeamStats {
  total_pending: number
  total_completed_today?: number  // Legacy field for backward compatibility
  total_completed_period?: number // New field that respects selected period
  leaderboard: Array<{
    email: string
    completed: number
    success_rate: number
  }>
  queue_status: {
    available: number
    locked: number
    total: number
  }
}

interface StatsState {
  personalStats: PersonalStats | null
  teamStats: TeamStats | null
  loading: boolean
  error: string | null
}

const initialState: StatsState = {
  personalStats: null,
  teamStats: null,
  loading: false,
  error: null,
}

export const fetchPersonalStats = createAsyncThunk(
  'stats/fetchPersonal',
  async (period: string = 'today') => {
    const response = await apiService.getPersonalStats(period)
    return response
  }
)

export const fetchTeamStats = createAsyncThunk(
  'stats/fetchTeam',
  async (period: string = 'today') => {
    const response = await apiService.getTeamStats(period)
    return response
  }
)

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Personal stats
      .addCase(fetchPersonalStats.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPersonalStats.fulfilled, (state, action) => {
        state.loading = false
        // Type assertion to handle partial API response
        const payload = action.payload as Partial<PersonalStats>
        state.personalStats = {
          leads_assigned: payload.leads_assigned ?? 0,
          leads_released: payload.leads_released ?? 0,
          leads_completed: payload.leads_completed ?? 0,
          leads_successful: payload.leads_successful ?? 0,
          total_worked_on: payload.total_worked_on ?? 0,
          success_rate: payload.success_rate ?? 0,
          release_rate: payload.release_rate ?? 0,
          average_time_minutes: payload.average_time_minutes ?? 0,
          total_emails_found: payload.total_emails_found ?? 0,
          by_source: payload.by_source ?? {
            palladium: 0,
            crm: 0,
            api: 0
          },
          queue_status: payload.queue_status ?? {
            available: 0,
            locked: 0,
            total: 0
          }
        }
        state.error = null
      })
      .addCase(fetchPersonalStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch personal stats'
      })
      // Team stats
      .addCase(fetchTeamStats.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchTeamStats.fulfilled, (state, action) => {
        state.loading = false
        state.teamStats = action.payload
        state.error = null
      })
      .addCase(fetchTeamStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch team stats'
      })
  },
})

export default statsSlice.reducer